// src/components/businesses/AIAgentConfig.tsx
// Panel simple para configurar el nombre del agente IA de la Org
// y el modelo LLM preferido. Se monta en Empresa → Configuración → tab "Agente IA".

import { useEffect, useState } from 'react';
import { Bot, Loader2, Save } from 'lucide-react';

import { ApiError, getAIAgentConfig, updateAIAgentConfig, type AIAgentConfig } from '@/lib/api_businesses';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

const MODEL_OPTIONS = [
  { value: '', label: 'Default (lo que esté en settings.AI_MARKETING_LLM_PROVIDER)' },
  { value: 'orion-default', label: 'Orion — default' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { value: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o mini' },
];

export function AIAgentConfigPanel({ addNotification }: { addNotification?: Notifier }) {
  const [cfg, setCfg] = useState<AIAgentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getAIAgentConfig().then(setCfg).catch((e) => {
      addNotification?.({
        type: 'error', title: 'No se pudo cargar',
        message: e instanceof ApiError ? e.message : e?.message,
      });
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      const out = await updateAIAgentConfig(cfg);
      setCfg(out);
      addNotification?.({ type: 'success', title: 'Configuración guardada' });
    } catch (e: any) {
      addNotification?.({
        type: 'error', title: 'No se pudo guardar',
        message: e instanceof ApiError ? e.message : e?.message,
      });
    } finally { setBusy(false); }
  };

  if (loading || !cfg) {
    return (
      <div className="py-8 flex justify-center text-white/40 text-sm">
        <Loader2 size={14} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-4">
      <div className="o-card space-y-3">
        <div className="flex items-center gap-2">
          <Bot size={18} style={{ color: 'var(--acc1)' }} />
          <h2 className="text-lg font-bold text-white">Agente IA</h2>
        </div>
        <p className="text-white/50 text-sm">
          El nombre y el modelo que usa el chatbot del dashboard + el generador automático de posts.
          Cada Org elige sus preferencias; si el modelo está vacío, se usa el default del provider configurado en el backend.
        </p>

        <label className="block">
          <span className="text-[11px] text-white/50 uppercase tracking-wider">Nombre del agente</span>
          <input
            className="input-base mt-1"
            value={cfg.ai_agent_name}
            onChange={(e) => setCfg({ ...cfg, ai_agent_name: e.target.value })}
            placeholder="Ej: María, Jarvis, Luna"
          />
        </label>

        <label className="block">
          <span className="text-[11px] text-white/50 uppercase tracking-wider">Foto del agente (URL)</span>
          <div className="flex items-center gap-3 mt-1">
            <img
              src={cfg.ai_agent_photo || `${import.meta.env.BASE_URL}assets/orion.png`}
              alt="avatar del agente"
              className="h-12 w-12 rounded-full object-cover border border-white/15 shrink-0"
            />
            <input
              className="input-base flex-1"
              value={cfg.ai_agent_photo ?? ''}
              onChange={(e) => setCfg({ ...cfg, ai_agent_photo: e.target.value })}
              placeholder="https://…/foto-del-agente.png (vacío = avatar por defecto)"
            />
          </div>
        </label>

        <label className="block">
          <span className="text-[11px] text-white/50 uppercase tracking-wider">Modelo LLM preferido</span>
          <select
            className="input-base mt-1"
            value={cfg.ai_preferred_model}
            onChange={(e) => setCfg({ ...cfg, ai_preferred_model: e.target.value })}
          >
            {MODEL_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </label>

        <button
          onClick={save}
          disabled={busy}
          className="px-4 py-2 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 text-[var(--acc1)] flex items-center gap-2"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
        </button>
      </div>
    </div>
  );
}

export default AIAgentConfigPanel;
